// IndexedDB-backed write queue for mutation endpoints. When the network
// is down or returns 5xx, we enqueue the request payload locally and
// replay in FIFO order when `online` fires. CSRF token is rebound at
// replay time (it lives in a cookie, so it survives reloads).
//
// Scope: POST/PUT/DELETE to a fixed allowlist of mutation paths. We do
// NOT queue arbitrary requests — taper plan changes, account deletion,
// and similar are intentionally fail-fast so the user sees an error
// rather than discovering 24 hours later that the change didn't land.
//
// Stale-write guard: items older than 24 hours are dropped on replay
// with a single user-visible toast. This prevents a forgotten queued
// dose log from re-landing weeks later and corrupting analytics.

const DB_NAME = 'qk-offline';
const DB_VERSION = 1;
const STORE = 'pending_mutations';
// 7-day stale-drop window. Was 24h, but a user who logged on a flight
// and didn't reopen the app until the next evening lost their dose.
// Safe to extend because:
//   1. The server's 60s idempotency dedup on POST /api/doses already
//      protects against late-arriving duplicate replays.
//   2. functions/api/doses/index.ts now rejects body.ts older than
//      7 days with 400 too_old (defense-in-depth pair with this
//      constant — they MUST stay numerically equal).
//   3. MAX_ITEMS = 50 caps unbounded growth regardless.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_ITEMS = 50;

export const QUEUEABLE_PATHS = [
  '/api/doses',
  '/api/logs',
  '/api/halt',
  '/api/me/settings',
  '/api/me/use-streak-freeze',
  '/api/popup/', // any /api/popup/:id/dismissed
] as const;

export interface QueuedMutation {
  id?: number;
  path: string;
  method: 'POST' | 'PUT' | 'DELETE';
  body: string | null;
  createdAt: number;
}

function isQueueablePath(path: string): boolean {
  return QUEUEABLE_PATHS.some((p) => path === p || path.startsWith(p));
}

async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueueMutation(item: Omit<QueuedMutation, 'id' | 'createdAt'>): Promise<void> {
  if (!isQueueablePath(item.path)) {
    throw new Error(`Path ${item.path} is not in the offline queue allowlist`);
  }
  const db = await openDB();
  const tx = db.transaction(STORE, 'readwrite');
  const store = tx.objectStore(STORE);
  store.add({ ...item, createdAt: Date.now() });
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
  // Background Sync — Chromium only. When the OS detects connectivity
  // (even with the PWA closed), it fires a `sync` event in the service
  // worker, which drains this same IDB store via the listener in
  // public/sw.js. Safari/Firefox lack SyncManager; they fall back to
  // the JS-layer drain in src/lib/sw.ts (visibilitychange / online /
  // 60s poll) on next app open.
  //
  // Re-registering the same tag is idempotent per the spec. Best-effort
  // — a failed register doesn't lose the dose because the app-side
  // wireOfflineQueue() drain still runs.
  await tryRegisterBackgroundSync();
}

// Minimal SyncManager shape — the W3C Background Sync API isn't in
// lib.dom by default (Safari/Firefox don't ship it), so we declare
// just the surface we touch. Matches the upstream IDL exactly.
interface SyncManagerLike {
  register(tag: string): Promise<void>;
  getTags(): Promise<string[]>;
}

async function tryRegisterBackgroundSync(): Promise<void> {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator) || !('SyncManager' in window)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const syncMgr = (reg as ServiceWorkerRegistration & { sync?: SyncManagerLike }).sync;
    await syncMgr?.register('drain-queue');
  } catch {
    // Best-effort.
  }
}

export async function getQueuedCount(): Promise<number> {
  const db = await openDB();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readonly');
    const count = tx.objectStore(STORE).count();
    count.onsuccess = () => {
      resolve(count.result);
      db.close();
    };
    count.onerror = () => {
      resolve(0);
      db.close();
    };
  });
}

async function getAllQueued(): Promise<QueuedMutation[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => {
      resolve(req.result as QueuedMutation[]);
      db.close();
    };
    req.onerror = () => {
      reject(req.error);
      db.close();
    };
  });
}

async function deleteQueued(id: number): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE, 'readwrite');
  tx.objectStore(STORE).delete(id);
  await new Promise<void>((resolve) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
  db.close();
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

interface DrainStats {
  ok: number;
  failed: number;
  dropped: number;
  /** Items left queued because the response was recoverable (401/408/429/5xx).
   * Separated from `failed` so the UI can surface "will retry" vs
   * "couldn't sync" honestly. */
  deferred: number;
}

// Status codes that should NOT drain the queue entry — they're
// transient and a later retry can land. 401 covers idle/sliding-session
// re-auth windows added in audit S4; 408/429 are explicit retry signals;
// 425 (Too Early) is rare but in spec for transient. Treating any of
// these as terminal silently loses the dose — that's the bug this
// guards against.
const RECOVERABLE_STATUS = new Set([401, 408, 425, 429]);

export async function drainQueue(): Promise<DrainStats> {
  const items = await getAllQueued();
  const stats: DrainStats = { ok: 0, failed: 0, dropped: 0, deferred: 0 };
  if (items.length === 0) return stats;
  // Cap at MAX_ITEMS oldest-first to avoid runaway replay.
  const sorted = [...items].sort((a, b) => a.createdAt - b.createdAt).slice(0, MAX_ITEMS);
  for (const item of sorted) {
    if (!item.id) continue;
    if (Date.now() - item.createdAt > MAX_AGE_MS) {
      await deleteQueued(item.id);
      stats.dropped++;
      continue;
    }
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (item.body) headers['Content-Type'] = 'application/json';
    // Queue only holds POST/PUT/DELETE; all need CSRF.
    const csrf = readCookie('csrf');
    if (csrf) headers['X-CSRF-Token'] = csrf;
    try {
      const res = await fetch(item.path, {
        method: item.method,
        headers,
        body: item.body,
        credentials: 'same-origin',
      });
      if (res.ok || res.status === 204) {
        // Persistence confirmed (or 204 idempotent re-replay). Done.
        await deleteQueued(item.id);
        stats.ok++;
      } else if (RECOVERABLE_STATUS.has(res.status) || res.status >= 500) {
        // Transient — keep queued so the next visibilitychange / online /
        // 60s poll retries after the user re-auths or the rate-limit
        // window passes. Without this, a 401 (session refresh window) or
        // 429 (rate-limit burst) silently destroys the user's data.
        stats.deferred++;
      } else {
        // Genuinely terminal 4xx (400 bad request, 403 csrf-or-forbid,
        // 404 endpoint gone, 410 gone, 413 payload too large, 422 schema
        // invalid). Replaying these will never succeed — drop and surface.
        await deleteQueued(item.id);
        stats.failed++;
      }
    } catch {
      // Network still bad — leave queued.
      stats.deferred++;
    }
  }
  return stats;
}

export function shouldOfflineEnqueue(err: unknown): boolean {
  if (!navigator.onLine) return true;
  // Any fetch-layer rejection counts as a network failure for queueing
  // purposes. On Android PWA in particular, fetch can reject with
  // DOMException, AbortError, or generic Error subclasses — not just
  // TypeError — when the WebView lifecycle, CORS preflight, DNS, or TLS
  // fails. Restricting to TypeError used to drop those on the floor.
  // ApiClientError (a non-network 4xx/5xx response) is not an Error
  // network failure — it's intentionally a separate class, so it still
  // surfaces to the caller for a real error toast.
  if (err instanceof Error && err.name !== 'ApiClientError') return true;
  return false;
}
